import {
    Component,
    computed,
    DOCUMENT,
    inject,
    input,
    InputSignal,
    linkedSignal,
    Signal,
    WritableSignal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';

import { StaticBlockWithTagsWizardSection, TagsWizardItem } from '@core/cms/landing.cms';
import { TrustHtmlPipe } from '@shared/pipes/trust-html/trust-html.pipe';

/**
 * Renders a `ComponentSectionStaticBlockWithTagsWizard`: the `static-block-with-tags` layout (intro
 * content beside a dark panel) where the panel is a **wizard** instead of a static chip list. It
 * starts on the first `wizardItems` entry; tags are multi-selectable and a Next button advances to
 * the next entry. The final entry carries no tags — when it declares a `scrollToAnchor` the panel
 * shows a button that scrolls to that section instead, labelled from `scrollToBtnLabel` (falling
 * back to `cms.wizard.scrollTo`).
 */
@Component({
    selector: 'cms-static-block-with-tags-wizard',
    imports: [MatButtonModule, TranslatePipe, TrustHtmlPipe],
    templateUrl: './static-block-with-tags-wizard.html',
    styleUrl: './static-block-with-tags-wizard.scss',
})
export class CmsStaticBlockWithTagsWizard {
    private readonly _document: Document = inject(DOCUMENT);

    public readonly section: InputSignal<StaticBlockWithTagsWizardSection> =
        input.required<StaticBlockWithTagsWizardSection>();

    /** Index of the visible step; resets to the first step when the section input changes. */
    protected readonly stepIndex: WritableSignal<number> = linkedSignal({
        source: this.section,
        computation: (): number => 0,
    });

    protected readonly currentStep: Signal<TagsWizardItem | undefined> = computed(
        (): TagsWizardItem | undefined => this.section().wizardItems[this.stepIndex()],
    );

    /** Set of selected tag ids for the current step; resets whenever the step changes. */
    protected readonly selectedTagIds: WritableSignal<Set<string>> = linkedSignal({
        source: this.stepIndex,
        computation: (): Set<string> => new Set<string>(),
    });

    protected readonly hasSelection: Signal<boolean> = computed(
        (): boolean => this.selectedTagIds().size > 0,
    );

    /** Toggles the selected state of a tag without advancing the step. */
    protected toggleTag(id: string): void {
        this.selectedTagIds.update((ids: Set<string>): Set<string> => {
            const next: Set<string> = new Set<string>(ids);

            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    }

    /** Advances to the next step and clears the current selection. */
    protected nextStep(): void {
        const lastIndex: number = this.section().wizardItems.length - 1;

        this.stepIndex.update((index: number): number => Math.min(index + 1, lastIndex));
    }

    /** Goes back to the previous step and clears the current selection. */
    protected prevStep(): void {
        this.stepIndex.update((index: number): number => Math.max(index - 1, 0));
    }

    /** Smooth-scrolls to the section the CMS pointed at; a missing target is simply a no-op. */
    protected scrollToAnchor(anchor: string): void {
        this.resolveAnchor(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /**
     * Resolves a `scrollToAnchor` value to an element. It is matched as an element **id** first, then
     * as a **class** — CMS authors point at bands by their `blockId` (e.g. `home__band--muted`), which
     * the dispatcher applies to the group wrapper as a class, not an id.
     */
    private resolveAnchor(anchor: string): Element | null {
        const name: string = anchor.trim().replace(/^#/, '');

        if (!name) {
            return null;
        }

        // `getElementsByClassName` rather than a `.${name}` selector: the CMS value is free text, so
        // a selector would need escaping (and `CSS.escape` is not guaranteed to exist everywhere).
        return this._document.getElementById(name) ?? this._document.getElementsByClassName(name)[0] ?? null;
    }
}
